import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Shield, Zap, BarChart3, Users, Phone, Brain } from 'lucide-react';

const TIERS = [
  {
    name: 'Recovery Core',
    price: '1,297',
    tier: 'recovery_core',
    description: 'Essential revenue recovery for single-location clinics',
    features: [
      'Missed call recovery',
      'AI follow-up messages',
      'No-show reactivation',
      'Consultation booking automation',
      'CRM pipeline',
      'Recovered revenue dashboard',
      '1,000 SMS / month',
      '1 Location',
    ],
    cta: 'Start Recovering Revenue',
    highlighted: false,
  },
  {
    name: 'Growth Accelerator',
    price: '1,997',
    tier: 'growth_accelerator',
    description: 'Advanced automation and reporting for growing practices',
    features: [
      'Everything in Recovery Core',
      'Treatment plan follow-up sequences',
      'Reactivation campaigns',
      'Lead scoring',
      'KPI reporting',
      'Advanced AI nurture',
      'Workflow customisation',
      '3,000 SMS / month',
      'Up to 3 Locations',
    ],
    cta: 'Accelerate Growth',
    highlighted: true,
  },
  {
    name: 'Premium Multi-Location',
    price: '2,997',
    tier: 'premium_multi_location',
    description: 'Enterprise solution for multi-location dental groups',
    features: [
      'Everything in Growth Accelerator',
      'Multi-location support',
      'Role-based team access',
      'Advanced dashboards',
      'Custom workflows',
      'White-glove support',
      '10,000 SMS / month',
      'Unlimited Locations',
    ],
    cta: 'Go Premium',
    highlighted: false,
  },
];

const FEATURES = [
  { icon: Phone, title: 'Missed Call Recovery', desc: 'Automatically text patients within 60 seconds of a missed call' },
  { icon: Brain, title: 'AI-Powered Follow-ups', desc: 'Generate personalized messages that feel human-written' },
  { icon: Users, title: 'Smart Pipeline', desc: '8-stage Kanban board tracks every patient from lead to case acceptance' },
  { icon: BarChart3, title: 'Revenue Leak Scanner', desc: 'Upload patient data and instantly see where money is being lost' },
  { icon: Zap, title: 'Automated Sequences', desc: 'No-show recovery, treatment follow-ups, and reactivation on autopilot' },
  { icon: Shield, title: 'HIPAA Compliant', desc: 'Enterprise-grade security with encrypted patient data at rest and in transit' },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-xl border-b border-slate-100 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
              <span className="text-white text-xs font-bold">DR</span>
            </div>
            <span className="text-lg font-bold text-slate-900 tracking-tight">Digital Reign</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Features</a>
            <a href="#pricing" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
            <a href="#about" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">About</a>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="nav-sign-in"
              onClick={() => navigate('/sign-in')}
              className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors px-3 py-2"
            >
              Sign In
            </button>
            <button
              data-testid="nav-get-started"
              onClick={() => navigate('/sign-up')}
              className="text-sm font-medium bg-slate-900 text-white px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-all duration-200"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-semibold mb-6 tracking-wide uppercase">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              Revenue Recovery Platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.1]">
              Stop Losing Revenue
              <br />
              <span className="text-slate-400">From Missed Calls & No-Shows</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              The all-in-one system that automatically recovers lost patients, reactivates
              unsold treatments, and fills your schedule — powered by AI and built for dental clinics.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <button
                data-testid="hero-get-started"
                onClick={() => navigate('/sign-up')}
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-7 py-3.5 rounded-lg font-medium hover:bg-slate-800 transition-all duration-200 text-sm"
              >
                Start Free Trial <ArrowRight size={16} />
              </button>
              <a
                href="#features"
                className="inline-flex items-center gap-2 border border-slate-200 text-slate-700 px-7 py-3.5 rounded-lg font-medium hover:bg-slate-50 transition-all duration-200 text-sm"
              >
                See How It Works
              </a>
            </div>
          </motion.div>

          {/* Hero image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 rounded-2xl overflow-hidden border border-slate-200 bg-white flex items-center justify-center py-16 px-12 shadow-2xl shadow-slate-200/50"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_3da4fd30-2c70-4250-9e4b-59e6541611f4/artifacts/f0td6ypj_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2010_48_49%20AM.png"
              alt="Digital Reign — Dental Revenue Recovery"
              className="w-auto h-auto max-h-[280px] object-contain"
            />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-emerald-600 mb-3">Features</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Everything You Need to Recover Lost Revenue
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl border border-slate-200 hover:-translate-y-1 hover:shadow-sm transition-all duration-200"
              >
                <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center mb-5">
                  <f.icon size={20} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-emerald-600 mb-3">Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Plans That Pay for Themselves
            </h2>
            <p className="mt-4 text-slate-600 max-w-xl mx-auto">
              Most clinics recover the cost of the platform within the first week.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {TIERS.map((tier, i) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                data-testid={`pricing-${tier.tier}`}
                className={`rounded-xl border p-8 flex flex-col ${
                  tier.highlighted
                    ? 'bg-slate-900 text-white border-slate-900 relative'
                    : 'bg-white border-slate-200'
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className={`text-lg font-semibold ${tier.highlighted ? 'text-white' : 'text-slate-900'}`}>
                  {tier.name}
                </h3>
                <p className={`text-sm mt-1 ${tier.highlighted ? 'text-slate-300' : 'text-slate-500'}`}>
                  {tier.description}
                </p>
                <div className="mt-6 mb-6">
                  <span className={`text-4xl font-bold tracking-tight ${tier.highlighted ? 'text-white' : 'text-slate-900'}`}>
                    ${tier.price}
                  </span>
                  <span className={`text-sm ${tier.highlighted ? 'text-slate-400' : 'text-slate-500'}`}>/month</span>
                </div>
                <ul className="space-y-3 flex-1 mb-8">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check size={16} className={`shrink-0 mt-0.5 ${tier.highlighted ? 'text-emerald-400' : 'text-emerald-500'}`} />
                      <span className={tier.highlighted ? 'text-slate-200' : 'text-slate-600'}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`pricing-cta-${tier.tier}`}
                  onClick={() => navigate(`/sign-up?tier=${tier.tier}`)}
                  className={`w-full py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    tier.highlighted
                      ? 'bg-white text-slate-900 hover:bg-slate-100'
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  {tier.cta}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About the Founder */}
      <section id="about" className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="relative w-full max-w-sm">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
                  <img
                    src="https://customer-assets.emergentagent.com/job_3da4fd30-2c70-4250-9e4b-59e6541611f4/artifacts/ddskacgo_IMG_20251022_082340_810.webp"
                    alt="Marcus Spicer — Founder of Digital Reign"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="mt-4">
                  <p className="text-base font-semibold text-slate-900">Marcus Spicer</p>
                  <p className="text-sm text-slate-500">Founder & CEO, Digital Reign LLC</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-emerald-600">About the Founder</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
                Built From a Problem<br />Seen Too Many Times
              </h2>
              <p className="text-base text-slate-600 leading-relaxed">
                Digital Reign was built after seeing the same silent problem happen again and again inside high-value service businesses: <span className="font-semibold text-slate-900">revenue was being lost in the gaps between patient interest and patient action.</span>
              </p>
              <div className="space-y-3 bg-white rounded-xl border border-slate-200 p-6">
                {[
                  'A missed call from a veneer lead.',
                  'A consultation request that never receives timely follow-up.',
                  'A patient who says they "need time to think" and is never nurtured back.',
                ].map((line, i) => (
                  <p key={i} className="text-sm text-slate-700 leading-relaxed pl-4 border-l-2 border-slate-300">
                    {line}
                  </p>
                ))}
              </div>
              <p className="text-base text-slate-600 leading-relaxed">
                For cosmetic and veneer-focused clinics, a single missed opportunity can represent thousands in lost case revenue. That realization became the foundation for Digital Reign.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                The goal is not more software. The goal is <span className="font-semibold text-slate-900">more booked consultations, more accepted treatment plans, and more recovered revenue.</span>
              </p>
              <button
                onClick={() => navigate('/about')}
                className="text-sm font-medium text-slate-900 underline underline-offset-4 hover:text-slate-600 transition-colors"
              >
                Read the full story
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-slate-900">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Ready to Stop Losing Revenue?
          </h2>
          <p className="mt-4 text-slate-400">
            Upload your patient data and see exactly where money is leaking from your practice — in minutes.
          </p>
          <button
            data-testid="cta-get-started"
            onClick={() => navigate('/sign-up')}
            className="mt-8 inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-3.5 rounded-lg font-medium hover:bg-slate-100 transition-all duration-200"
          >
            Get Started Now <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-100">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-slate-900 rounded flex items-center justify-center">
              <span className="text-white text-[8px] font-bold">DR</span>
            </div>
            <span className="text-sm font-semibold text-slate-900">Digital Reign</span>
          </div>
          <p className="text-xs text-slate-500">Dental Revenue Recovery</p>
        </div>
      </footer>
    </div>
  );
}
